import { useEffect, useMemo, useState } from 'react';

import { HoroscopeIcon } from './HoroscopeIcon';
import {
  buildBirthYearOptions,
  saveDemographics,
} from '../services/demographicsService';
import type {
  AccountUserProfileHoroscope,
  AccountUserProfileRecord,
} from '../services/accountUserProfileService';
import {
  getAllHoroscopes,
  getHoroscopeMeta,
  getHoroscopesForSeason,
  inferSeasonFromHoroscope,
} from '../utils/horoscopeCatalog';

type DemographicsPromptModalProps = {
  visible: boolean;
  userId: string;
  initialProfile: AccountUserProfileRecord | null;
  onDismiss: () => void;
  onSaved: () => void;
};

export function DemographicsPromptModal({
  visible,
  userId,
  initialProfile,
  onDismiss,
  onSaved,
}: DemographicsPromptModalProps) {
  const yearOptions = useMemo(() => buildBirthYearOptions(), []);
  const hasPresetYearAndSeason = initialProfile?.birthYear != null && initialProfile?.birthSeason != null;

  const [birthYear, setBirthYear] = useState<number | null>(initialProfile?.birthYear ?? null);
  const [horoscope, setHoroscope] = useState<AccountUserProfileHoroscope | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!visible) {
      return;
    }
    setBirthYear(initialProfile?.birthYear ?? null);
    setHoroscope(null);
    setError(null);
  }, [initialProfile?.birthYear, visible]);

  const horoscopeOptions = useMemo(() => {
    if (hasPresetYearAndSeason && initialProfile?.birthSeason) {
      return getHoroscopesForSeason(initialProfile.birthSeason);
    }
    return getAllHoroscopes();
  }, [hasPresetYearAndSeason, initialProfile?.birthSeason]);

  const resolvedYear = birthYear ?? initialProfile?.birthYear ?? null;
  const canSubmit = resolvedYear != null && horoscope != null && !saving;
  const selectedHoroscopeMeta = horoscope ? getHoroscopeMeta(horoscope) : null;

  if (!visible) {
    return null;
  }

  const handleSubmit = () => {
    if (resolvedYear == null || horoscope == null) {
      return;
    }

    void (async () => {
      setSaving(true);
      setError(null);
      try {
        await saveDemographics(userId, {
          birthYear: resolvedYear,
          birthSeason: initialProfile?.birthSeason ?? inferSeasonFromHoroscope(horoscope),
          horoscope,
        });
        onSaved();
      } catch (submitError) {
        setError(submitError instanceof Error ? submitError.message : '保存失败，请稍后重试');
      } finally {
        setSaving(false);
      }
    })();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <button
        type="button"
        className="absolute inset-0 cursor-default"
        aria-label="关闭"
        onClick={onDismiss}
      />
      <div className="relative z-10 flex max-h-[90vh] w-full max-w-lg flex-col overflow-hidden rounded-2xl border border-border bg-surface shadow-xl">
        <div className="flex-1 overflow-y-auto p-6 pb-4">
          <h2 className="text-xl font-extrabold text-ink">完善学习画像</h2>
          <p className="mt-2 text-sm leading-6 text-muted">
            用于更好地匹配学习难度与节奏。请选择你的星座——复习获得的奖励宝石将镶嵌在此星座上。
          </p>

          {!hasPresetYearAndSeason ? (
          <div className="mt-4 space-y-2">
            <p className="text-base font-bold text-ink">你出生在</p>
            <div className="flex gap-2 overflow-x-auto pb-1">
              {yearOptions.map((year) => {
                const selected = birthYear === year;
                return (
                  <button
                    key={year}
                    type="button"
                    onClick={() => setBirthYear(year)}
                    className={[
                      'min-w-[72px] rounded-full border px-4 py-2 text-base font-bold',
                      selected
                        ? 'border-primary bg-[#EAF2FF] text-primary-strong'
                        : 'border-border bg-canvas text-ink',
                    ].join(' ')}
                  >
                    {year}
                  </button>
                );
              })}
            </div>
          </div>
        ) : null}

        {(hasPresetYearAndSeason || birthYear != null) ? (
          <div className="mt-4 space-y-2">
            <p className="text-base font-bold text-ink">选择你的星座</p>
            <p className="text-xs text-muted">
              {hasPresetYearAndSeason
                ? '根据你已填写的出生季节，以下星座可供选择'
                : '选择星座后，系统会自动推断你的出生季节'}
            </p>
            <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
              {horoscopeOptions.map((option) => {
                const selected = horoscope === option.id;
                return (
                  <button
                    key={option.id}
                    type="button"
                    onClick={() => setHoroscope(option.id)}
                    className={[
                      'flex flex-col items-center gap-1 rounded-xl border px-2 py-3 transition',
                      selected
                        ? 'border-primary bg-[#EAF2FF]'
                        : 'border-border bg-canvas hover:border-primary/40',
                    ].join(' ')}
                  >
                    <HoroscopeIcon horoscope={option.id} size={40} selected={selected} />
                    <span className={['text-sm font-bold', selected ? 'text-primary-strong' : 'text-ink'].join(' ')}>
                      {option.label}
                    </span>
                    <span className={['text-[11px]', selected ? 'text-primary-strong' : 'text-muted'].join(' ')}>
                      {option.dateRange}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        ) : null}

        {selectedHoroscopeMeta && resolvedYear != null ? (
          <p className="mt-4 text-sm font-semibold text-primary-strong">
            {hasPresetYearAndSeason
              ? `你的星座是 ${selectedHoroscopeMeta.label}，奖励宝石将镶嵌在此星座上`
              : `你出生在 ${resolvedYear} 年，星座 ${selectedHoroscopeMeta.label}，奖励宝石将镶嵌在此星座上`}
          </p>
        ) : null}

          {error ? <p className="mt-3 text-sm text-danger">{error}</p> : null}
        </div>

        <div className="shrink-0 space-y-2 border-t border-border bg-surface p-6 pt-4">
          <button
            type="button"
            onClick={onDismiss}
            className="w-full rounded-full border border-border px-4 py-2.5 text-sm font-medium text-ink"
          >
            稍后填写
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="w-full rounded-full bg-primary px-4 py-2.5 text-sm font-medium text-white disabled:cursor-not-allowed disabled:opacity-50"
          >
            {saving ? '保存中...' : '确认'}
          </button>
        </div>
      </div>
    </div>
  );
}
